/**
 * Hidden `<audio>` element kept mounted for the app's lifetime so play()
 * never races attachAudio() (the old Player only rendered audio when a
 * track was visible, so the ref was often still null on the first click).
 */
import { useCallback, useEffect, useRef } from "react";
import { qualifiesAsPlay } from "@memes/shared";
import { playbackApi } from "../lib/api";
import {
  applyPlaybackApiSnapshot,
  isApplyingRemotePlayback,
  markLocalPlaybackControl,
} from "../lib/playbackSync";
import { usePlayer, mediaUrl, applyVoiceOutputMode } from "../state/player";
import { resumePlaybackAudioContext, useAudioGraph } from "../hooks/useAudioGraph";
import { useEndSession, useVoiceStatus } from "../hooks/useStats";
import { useMarkPlayed } from "../hooks/useTracks";
import { clientLog } from "../lib/clientLogger";
import {
  absoluteCoverUrl,
  installMediaSessionHandlers,
  mediaSessionArtwork,
  shouldHonorMediaSessionPause,
  updateMediaSessionPlayback,
} from "../lib/mediaSession";
import {
  nextBackgroundAdvance,
  runBackgroundAdvance,
  shouldTreatElementPauseAsUserPause,
} from "../lib/backgroundAdvance";

function resolvedMediaSrc(track: Parameters<typeof mediaUrl>[0]): string {
  return new URL(mediaUrl(track), window.location.href).href;
}

function assignTrackSrc(el: HTMLAudioElement, track: Parameters<typeof mediaUrl>[0]): void {
  const target = resolvedMediaSrc(track);
  if (el.src !== target) {
    el.src = mediaUrl(track);
  }
}

function updateDurationFromElement(el: HTMLAudioElement): void {
  const d = el.duration;
  if (Number.isFinite(d) && d > 0) {
    usePlayer.setState({ duration: d });
  }
}

function resolveEffectiveDuration(
  stateDuration: number | null,
  trackDurationSec: number | null | undefined,
  el: HTMLAudioElement | null,
): number | null {
  if (stateDuration != null && stateDuration > 0) return stateDuration;
  if (trackDurationSec != null && trackDurationSec > 0) return trackDurationSec;
  if (el) {
    const d = el.duration;
    if (Number.isFinite(d) && d > 0) return d;
  }
  return null;
}

export const AudioEngine: React.FC = () => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const attachAudio = usePlayer((s) => s.attachAudio);
  const setVoiceConnected = usePlayer((s) => s.setVoiceConnected);
  const queue = usePlayer((s) => s.queue);
  const index = usePlayer((s) => s.index);
  const isPlaying = usePlayer((s) => s.isPlaying);
  const volume = usePlayer((s) => s.volume);
  const muted = usePlayer((s) => s.muted);
  const voiceConnected = usePlayer((s) => s.voiceConnected);
  const track = queue[index] ?? null;
  const audio = usePlayer((s) => s.audio);
  useAudioGraph(audio, track);
  const endSession = useEndSession();
  const markPlayed = useMarkPlayed();
  const voiceStatus = useVoiceStatus();
  const advancedTrackIdRef = useRef<number | null>(null);
  const advancingRef = useRef(false);

  useEffect(() => {
    attachAudio(audioRef.current);
    return () => attachAudio(null);
  }, [attachAudio]);

  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    return installMediaSessionHandlers(navigator.mediaSession, {
      play: () => usePlayer.getState().play(),
      pause: () => {
        const el = usePlayer.getState().audio;
        if (
          !shouldHonorMediaSessionPause({
            elementEnded: !!el?.ended,
            advancing: advancingRef.current,
          })
        ) {
          return;
        }
        usePlayer.getState().pause();
      },
      next: () => usePlayer.getState().next(),
      previous: () => usePlayer.getState().prev(),
    });
  }, []);

  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    const s = usePlayer.getState();
    updateMediaSessionPlayback(navigator.mediaSession, {
      playing: isPlaying,
      durationSec: s.duration,
      positionSec: s.currentTime,
    });
  }, [isPlaying, track]);

  useEffect(() => {
    if (!("mediaSession" in navigator) || typeof MediaMetadata === "undefined") return;
    navigator.mediaSession.metadata = track
      ? new MediaMetadata({
          title: track.metadataTitle ?? track.name,
          artist: track.metadataArtist ?? track.owner,
          album: "Memes & Shit Shuffle",
          artwork: mediaSessionArtwork(
            absoluteCoverUrl(track.id, window.location.origin),
          ),
        })
      : null;
  }, [track]);

  useEffect(() => {
    setVoiceConnected(voiceStatus.data?.connected ?? false);
  }, [voiceStatus.data?.connected, setVoiceConnected]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    applyVoiceOutputMode(el, voiceConnected, volume, muted);
    if (voiceConnected) el.pause();
  }, [voiceConnected, volume, muted]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el || !track) return;
    assignTrackSrc(el, track);
    usePlayer.setState({ duration: track.durationSec ?? null });
  }, [track?.id, track?.filename]);

  const playNextLocally = useCallback((nextTrack: (typeof queue)[number], nextIndex: number): void => {
    const el = audioRef.current;
    if (!el) return;
    advancingRef.current = true;
    markLocalPlaybackControl();
    assignTrackSrc(el, nextTrack);
    applyVoiceOutputMode(el, false, 1, usePlayer.getState().muted);
    el.playbackRate = 1;
    resumePlaybackAudioContext();
    usePlayer.setState({
      index: nextIndex,
      isPlaying: true,
      currentTime: 0,
      duration: nextTrack.durationSec ?? null,
    });
    void el
      .play()
      .catch((err) => {
        clientLog.error("playback", "Next-track play failed", { err: String(err) });
      })
      .finally(() => {
        advancingRef.current = false;
      });
  }, []);

  const handleTrackFinished = useCallback(() => {
    const el = audioRef.current;
    const { queue: q, index: idx, voiceConnected: inVoice, activeOwners } = usePlayer.getState();
    const t = q[idx];
    if (inVoice) return;

    if (t && advancedTrackIdRef.current === t.id) return;
    if (t) advancedTrackIdRef.current = t.id;

    const listened = el?.currentTime ?? 0;
    const nxt = t
      ? nextBackgroundAdvance({ queue: q, index: idx, activeOwners })
      : null;

    runBackgroundAdvance({
      playNext: () => {
        if (nxt) playNextLocally(nxt.track, nxt.index);
      },
      notifyServer: () =>
        playbackApi
          .next()
          .then((res) => applyPlaybackApiSnapshot(res.snapshot, true))
          .catch((err) => {
            clientLog.error("playback", "Auto-advance to next track failed", { err: String(err) });
            advancedTrackIdRef.current = null;
          }),
      recordStats: async () => {
        if (!t) return;
        try {
          await endSession.mutateAsync(listened);
          if (qualifiesAsPlay(listened, t.durationSec)) {
            void markPlayed.mutateAsync({ id: t.id });
          }
        } catch (err) {
          clientLog.error("playback", "End session failed", { err: String(err) });
        }
      },
    });
  }, [endSession, markPlayed, playNextLocally]);

  useEffect(() => {
    if (!isPlaying || voiceConnected) return;
    const id = setInterval(() => {
      const s = usePlayer.getState();
      const el = audioRef.current;
      const t = s.queue[s.index];
      const dur = resolveEffectiveDuration(s.duration, t?.durationSec, el);
      const ct = s.voiceConnected ? s.currentTime : (el?.currentTime ?? s.currentTime);
      if ("mediaSession" in navigator) {
        updateMediaSessionPlayback(navigator.mediaSession, {
          playing: s.isPlaying,
          durationSec: dur,
          positionSec: ct,
        });
      }
      if (!s.isPlaying || isApplyingRemotePlayback()) return;
      if (dur == null) return;
      if (ct < dur - 0.5) return;
      handleTrackFinished();
    }, 1_000);
    return () => clearInterval(id);
  }, [isPlaying, voiceConnected, handleTrackFinished]);

  return (
    <audio
      ref={audioRef}
      className="hidden"
      preload="metadata"
      onTimeUpdate={(e) => {
        if (!isPlaying || isApplyingRemotePlayback() || voiceConnected) return;
        const el = e.target as HTMLAudioElement;
        const dur = el.duration;
        if (!Number.isFinite(dur) || dur <= 0) return;
        if (el.currentTime < dur - 0.4) return;
        handleTrackFinished();
      }}
      onLoadedMetadata={(e) => {
        updateDurationFromElement(e.target as HTMLAudioElement);
      }}
      onDurationChange={(e) => {
        updateDurationFromElement(e.target as HTMLAudioElement);
      }}
      onEnded={() => {
        if (!voiceConnected) handleTrackFinished();
      }}
      onPlay={() => {
        if (!isApplyingRemotePlayback()) usePlayer.setState({ isPlaying: true });
      }}
      onPause={(e) => {
        const el = e.currentTarget;
        if (
          !shouldTreatElementPauseAsUserPause({
            documentHidden: document.hidden,
            applyingRemote: isApplyingRemotePlayback(),
            advancing: advancingRef.current,
            elementEnded: el.ended,
          })
        ) {
          return;
        }
        usePlayer.setState({ isPlaying: false });
      }}
      onError={() => {
        usePlayer.setState({ isPlaying: false });
        clientLog.error("playback", "Audio failed to load", {
          track: track ? mediaUrl(track) : "(no track)",
        });
      }}
    />
  );
};
